import React, { useState, useEffect } from "react"
import { isPossiblePhoneNumber } from "react-phone-number-input"
import { useMutation } from "urql"

import { InputWithLabel } from "../../components/shared/Inputs"
import InternationalPhoneInput from "../../components/shared/InternationalPhoneInput"
import Typography from "../../components/shared/Typography"
import { useToast } from "../../contexts/ToastContext"
import ReviewProvider from "../../providers/ReviewProvider"

import { ReviewLayout } from "./ReviewLayout"

const CREATE_CLIENT_MUTATION = `
  mutation ReviewCreateClient($practiceId: ID!, $phone: String!, $firstName: String!) {
    reviewCreateClient(practiceId: $practiceId, phone: $phone, firstName: $firstName) {
      result
      clientId
      errors
    }
  }
`

const ClientInfo = ({ review }) => {
  const { showToast } = useToast()
  const [firstName, setFirstName] = useState("")
  const [phone, setPhone] = useState("")
  const [{ fetching }, createClient] = useMutation(CREATE_CLIENT_MUTATION)
  const [createdClientInfo, setCreatedClientInfo] = useState(null)

  useEffect(() => {
    const storedClientInfo = localStorage.getItem("createdClientInfo")
    if (storedClientInfo) {
      setCreatedClientInfo(JSON.parse(storedClientInfo))
    }
  }, [])

  const hasCreatedClient = Boolean(createdClientInfo?.id)
  const isFormValid = hasCreatedClient || (firstName.trim().length >= 2 && isPossiblePhoneNumber(phone || ""))

  const handleCreateClient = () => {
    if (createdClientInfo?.id) {
      window.location.href = `/i/r/rating?referral_code=${review.referralCode}&client_id=${createdClientInfo.id}`
    } else {
      if (fetching) return

      createClient({ practiceId: review.practiceId, phone, firstName }).then((res) => {
        if (res.error || !res.data) {
          showToast({ content: "Something went wrong. Please try again.", type: "error" })
          return
        }
        localStorage.setItem(
          "createdClientInfo",
          JSON.stringify({
            firstName: firstName,
            phone: phone,
            id: res.data.reviewCreateClient.clientId
          })
        )
        if (res.data.reviewCreateClient.result === "success") {
          window.location.href = `/i/r/rating?referral_code=${review.referralCode}&client_id=${res.data.reviewCreateClient.clientId}`
        } else {
          showToast({
            content: `Error creating client: Please enter a valid phone number to continue and leave a review.`,
            type: "error"
          })
        }
      })
    }
  }

  return (
    <ReviewLayout percentDone="30%" onContinue={handleCreateClient} continueDisabled={!isFormValid || fetching}>
      <Typography variant="h3">Oh great, thank you for that!</Typography>
      <Typography variant="body">Before we continue, can you tell us who you are:</Typography>
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-1">
        <InputWithLabel
          label="First name"
          disabled={hasCreatedClient}
          value={hasCreatedClient ? createdClientInfo.firstName : firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <InternationalPhoneInput
          inputId="review-client-phone"
          label="Phone"
          disabled={hasCreatedClient}
          value={hasCreatedClient ? createdClientInfo.phone : phone}
          onChange={setPhone}
        />
      </div>
    </ReviewLayout>
  )
}

export default function ReviewClientInfo(props) {
  return (
    <ReviewProvider>
      <ClientInfo {...props} />
    </ReviewProvider>
  )
}
